// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/shared/SettingsRow.module.css';
import {CaretRightIcon} from '@app/features/ui/icons/Icons';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId} from 'react';

export interface SettingsRowIds {
	labelId: string;
	descriptionId: string | undefined;
}

interface SettingsRowProps {
	id?: string;
	/** 40px round tile on the left. */
	icon?: React.ReactNode;
	title: React.ReactNode;
	description?: React.ReactNode;
	/** The control on the right; receives ids so it can point aria-labelledby/-describedby at the text. */
	control: (ids: SettingsRowIds) => React.ReactNode;
	/** Clicking the text runs this (e.g. toggles the row's switch). */
	onActivate?: () => void;
	disabled?: boolean;
	className?: string;
	'data-flx'?: string;
}

/**
 * KONVO round 6: a grouped setting row (icon tile, title and muted line, control on the right).
 * Consecutive rows group into one rounded block (SettingsRows.css).
 */
// An observer: `control` is called during this render, so the values it reads (a MobX preference,
// say) must re-render the row when they change, or the control keeps showing the old choice.
export const SettingsRow: React.FC<SettingsRowProps> = observer(
	({id, icon, title, description, control, onActivate, disabled, className, 'data-flx': dataFlx}) => {
		const baseId = useId();
		const labelId = `${baseId}-label`;
		const descriptionId = description ? `${baseId}-description` : undefined;
		const text = (
			<>
				<span id={labelId} className={styles.title} data-flx="app.settings-row.title">
					{title}
				</span>
				{description ? (
					<span id={descriptionId} className={styles.description} data-flx="app.settings-row.description">
						{description}
					</span>
				) : null}
			</>
		);
		return (
			<div
				id={id}
				className={clsx(styles.row, disabled && styles.disabled, className)}
				data-settings-row=""
				data-flx={dataFlx ?? 'app.settings-row.row'}
			>
				{icon ? (
					<span className={styles.icon} aria-hidden="true" data-flx="app.settings-row.icon">
						{icon}
					</span>
				) : null}
				{onActivate ? (
					// Mouse convenience only: the control itself is the keyboard and screen-reader target.
					<button
						type="button"
						tabIndex={-1}
						aria-hidden="true"
						className={clsx(styles.text, styles.textButton, !disabled && styles.clickable)}
						disabled={disabled}
						onClick={onActivate}
						data-flx="app.settings-row.text"
					>
						{text}
					</button>
				) : (
					<div className={styles.text} data-flx="app.settings-row.text">
						{text}
					</div>
				)}
				<div className={styles.control} data-flx="app.settings-row.control">
					{control({labelId, descriptionId})}
				</div>
			</div>
		);
	},
);

interface SettingsLinkRowProps {
	id?: string;
	icon?: React.ReactNode;
	title: React.ReactNode;
	description?: React.ReactNode;
	/** Right side; defaults to a chevron ("opens something"). */
	trailing?: React.ReactNode;
	onClick: () => void;
	disabled?: boolean;
	className?: string;
	'aria-label'?: string;
	'data-flx'?: string;
}

/** KONVO: a grouped row that is one button (chevron on the right), for rows that open something. */
export const SettingsLinkRow: React.FC<SettingsLinkRowProps> = ({
	id,
	icon,
	title,
	description,
	trailing,
	onClick,
	disabled,
	className,
	'aria-label': ariaLabel,
	'data-flx': dataFlx,
}) => (
	<button
		id={id}
		type="button"
		className={clsx(styles.row, styles.linkRow, disabled && styles.disabled, className)}
		onClick={onClick}
		disabled={disabled}
		aria-label={ariaLabel}
		data-settings-row=""
		data-flx={dataFlx ?? 'app.settings-row.link-row'}
	>
		{icon ? (
			<span className={styles.icon} aria-hidden="true" data-flx="app.settings-row.icon">
				{icon}
			</span>
		) : null}
		<span className={styles.text} data-flx="app.settings-row.text">
			<span className={styles.title} data-flx="app.settings-row.title">
				{title}
			</span>
			{description ? (
				<span className={styles.description} data-flx="app.settings-row.description">
					{description}
				</span>
			) : null}
		</span>
		<span className={clsx(styles.control, styles.linkTrailing)} data-flx="app.settings-row.control">
			{trailing ?? <CaretRightIcon size={18} weight="bold" />}
		</span>
	</button>
);
